import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, Button, Card, CircularProgress, Typography } from '@mui/material';
import JourneyAppPromo from '~/components/StandardJourney/JourneyAppPromo';
import StatusIcon from '~/components/StandardJourney/JourneyStatusIcon';
import { blue, neutral } from '~/styles/tokens/primitives.colors';
import { radius } from '~/styles/tokens/shape';
import { RENEWAL_APP_IDS } from '../renewalAppIds';
import type { RenewalOutcome } from '../renewalPayment';

type Props = {
  outcome: Exclude<RenewalOutcome, 'outdated'>;
  /** E-mail do segurado, para «Enviamos o comprovante para …». */
  email: string;
  /** Mensagem da API (4xx com `notifications`) para a falha; sem ela, só o texto padrão. */
  errorMessage: string | null;
  retrying: boolean;
  onRetry: () => void;
  onUseAnotherCard: () => void;
};

const cardSx = {
  alignItems: 'center',
  borderRadius: `${radius.large}px`,
  boxShadow: '0px 1px 1px rgba(0,0,0,0.3), 0px 2px 3px rgba(0,0,0,0.15)',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  p: 3,
  textAlign: 'center',
  width: '100%'
} as const;

/**
 * Desfechos da renovação 2026, sem «Voltar»: concluída (Figma 2091:5122), em análise pela seguradora
 * (2096:5480) e falha no pagamento (2091:5331). A renovação fora do prazo chega aqui como concluída depois
 * da etapa de fotos (`RenewalPhotosStep`).
 */
export default function RenewalOutcomeStep({
  outcome,
  email,
  errorMessage,
  retrying,
  onRetry,
  onUseAnotherCard
}: Props) {
  if (outcome === 'failure') {
    return (
      <Box
        data-app-id={RENEWAL_APP_IDS.outcome}
        data-outcome="failure"
        sx={{ maxWidth: 560, mx: 'auto', width: '100%' }}
      >
        <Card elevation={0} sx={cardSx}>
          <StatusIcon kind="error" />
          <Typography component="h1" variant="h5">
            Não foi possível concluir o pagamento
          </Typography>
          {errorMessage ? (
            <Typography data-app-id={`${RENEWAL_APP_IDS.outcome}-api-message`} sx={{ fontWeight: 600 }} variant="body1">
              {errorMessage}
            </Typography>
          ) : null}
          <Typography variant="body1">
            Nenhum valor foi cobrado. Tente novamente em alguns instantes ou use outro cartão. Se o problema continuar,
            entre em contato com o seu banco ou com o nosso atendimento.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <Button
              data-app-id={`${RENEWAL_APP_IDS.outcome}-retry`}
              disabled={retrying}
              fullWidth
              onClick={onRetry}
              size="large"
              startIcon={retrying ? <CircularProgress aria-hidden color="inherit" size={18} /> : undefined}
              sx={{ borderRadius: `${radius.large}px` }}
              variant="contained"
            >
              {retrying ? 'Tentando novamente' : 'Tentar novamente'}
            </Button>
            <Button
              data-app-id={`${RENEWAL_APP_IDS.outcome}-another-card`}
              disabled={retrying}
              fullWidth
              onClick={onUseAnotherCard}
              size="large"
              sx={(theme) => ({
                borderColor: theme.customColors.tertiaryMain,
                borderRadius: `${radius.large}px`,
                color: theme.customColors.tertiaryMain
              })}
              variant="outlined"
            >
              Usar outro cartão
            </Button>
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      data-app-id={RENEWAL_APP_IDS.outcome}
      data-outcome={outcome}
      sx={{ display: 'flex', flexDirection: 'column', gap: { md: 8, xs: 4 }, maxWidth: 699, mx: 'auto', width: '100%' }}
    >
      <Card elevation={0} sx={cardSx}>
        <StatusIcon kind="success" />
        {outcome === 'analysis' ? (
          <>
            <Typography component="h1" variant="h5">
              Contratação concluída com sucesso!
            </Typography>
            <Typography variant="subtitle1">
              Seu pagamento foi aprovado e o contrato está sendo analisado pela seguradora. Em breve você receberá a
              confirmação no seu e-mail.
            </Typography>
            <Box
              sx={{
                alignItems: 'flex-start',
                bgcolor: blue[100],
                borderRadius: `${radius.medium}px`,
                display: 'flex',
                gap: 2,
                p: 2,
                textAlign: 'left',
                width: '100%'
              }}
            >
              <InfoOutlined aria-hidden sx={{ color: neutral[1100], flexShrink: 0, mt: 0.25 }} />
              <Typography sx={{ color: neutral[1100] }} variant="body1">
                A análise é feita em até 15 dias úteis. Reforçamos a cobertura completa do seguro e seus benefícios
                durante o período de análise.
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Typography component="h1" variant="h5">
              Renovação concluída!
            </Typography>
            <Typography variant="subtitle1">
              {`Sua proteção segue ativa por mais 12 meses. Enviamos o comprovante para ${email || 'o seu e-mail'}.`}
            </Typography>
          </>
        )}
      </Card>
      <JourneyAppPromo appId={RENEWAL_APP_IDS.outcome} />
    </Box>
  );
}
